/*
    Ejemplo de uso:

    1. Importar el hook
        import { useApiFetcher } from 'utils/fetcher/ApiFetcher';

    2. Dentro de un componente React
        const apiFetcher = useApiFetcher();
  
    3. Llamada a la API con JSON data
        apiFetcher({
            type: 'POST',
            url: "?section=sandbox&action=Test",
            data: {
                //Parametros a enviar a la API
            },
            showSuccessNotification: true,
            showErrorNotification: true,
            showSuccessNotificationModal: true,
            showErrorNotificationModal: true,
            onSuccess: (responseData)=>{
                // Aquí puedes manejar la respuesta exitosa
            },
            onError: (responseData)=>{
                // Aquí puedes manejar el error
            },
        });

    4. Llamada a la API con FormData (para subir archivos)
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('id', '123');
        
        apiFetcher({
            type: 'POST',
            url: "?section=sandbox&action=UploadFile",
            data: formData,
            showSuccessNotification: true,
            showErrorNotification: true,
            onSuccess: (responseData)=>{
                // Archivo subido exitosamente
            },
            onError: (error)=>{
                // Error al subir archivo
            },
        });
*/




import { openSnackbar } from 'api/snackbar';
import { createContext, useContext, useCallback } from 'react';
import { useNotificationModal } from 'components/worten/notification-modal';





// Create a context to store the fetcher function
const ApiFetcherContext = createContext(null);



// This is the React component that provides the fetcher function
export function ApiFetcherProvider({ children }) {
  const { openNotificationModal } = useNotificationModal();

  const fetchData = useCallback(async ({ 
    url, 
    type, 
    data = null, 
    cache = 'no-store',
    showSuccessNotification = false,
    showErrorNotification = false,
    showSuccessNotificationModal = false,
    showErrorNotificationModal = false,
    onSuccess = null, 
    onError = null,
    fileType = 'json',
    filename = "document",
  }) => {
  const baseURL = (process.env.REACT_APP_PRODUCTION || '').toUpperCase() === 'TRUE' 
      ? process.env.REACT_APP_API_URL_PRODUCTION + process.env.REACT_APP_STORE_ID + '/api/'
      : process.env.REACT_APP_API_URL_PRODUCTION + process.env.REACT_APP_STORE_ID + '/api_dev/';    

    const options = {
      method: type,
      credentials: 'include',
      cache,
      headers: {},
    };

    // Handle FormData for file uploads
    if (data instanceof FormData) {
      options.body = data;
    } else if (data) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(data);
    } else {
      options.headers['Content-Type'] = 'application/json';
    }

    // IMPORTANT: For FormData, we should NOT set Accept header to anything other than json
    // when expecting a JSON response, so let's modify this:
    if (fileType && fileType !== 'json') {
      options.headers['Accept'] = fileType === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        : fileType === 'pdf' 
        ? 'application/pdf' 
        : 'application/json';
      options.responseType = 'blob';
    }

    
    const OpenNotification = (message, severity) => {
      if((severity === 'success' && showSuccessNotificationModal) || (severity === 'error' && showErrorNotificationModal)) 
      {
        openNotificationModal({
            message: message,
            type: severity,
        });
        return;
      }

      
      if((severity === 'success' && showSuccessNotification) || (severity === 'error' && showErrorNotification)) 
      {
        openSnackbar({
            open: true,
            message: message,
            alert: {
              color: severity,
              severity: severity
            }
        });
      }
    };

    try {
      const response = await fetch(baseURL + url, options);
      console.log(response);

      //Handle file downloads
      if (fileType !== 'json') {
        if (!response.ok) {
          const errorText = await response.text();
          OpenNotification(errorText || `HTTP error! status: ${response.status}`, 'error');
          return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = fileType === 'excel' ? filename+'.xlsx' : filename+'document.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }


      //Handle JSON responses
      let result;
      const responseText = await response.text();
      
      // Check if response contains raw error messages (like PHP warnings/errors)
      const hasPhpWarning = responseText.includes('<b>Warning</b>') || 
          responseText.includes('<b>Notice</b>');
      const hasPhpError = responseText.includes('<b>Error</b>') || 
          responseText.includes('<b>Fatal error</b>');

      if (hasPhpWarning || hasPhpError) {
        // Try to extract JSON from the response (warnings may be prepended before valid JSON)
        const jsonStart = responseText.indexOf('{');
        if (!hasPhpError && jsonStart !== -1) {
          const jsonText = responseText.substring(jsonStart);
          try {
            const parsed = JSON.parse(jsonText);
            console.warn("PHP warning in response (ignored, JSON extracted):", responseText.substring(0, jsonStart));
            // Replace responseText with the cleaned JSON and continue normal flow
            result = parsed;
          } catch {
            // Fall through to hard error
          }
        }

        if (!result) {
          console.log("Raw error detected in response:", responseText);
          let errorMessage = "Error no identificado:" + responseText;
          if (errorMessage.length > 500) {
            errorMessage = errorMessage.substring(0, 500) + '...';
          }
          OpenNotification(errorMessage, 'error');
          if (onError) {
            onError({ message: errorMessage, rawResponse: responseText });
          }
          return;
        }
      }

      if (!result) {
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.log("Failed to parse JSON response:", responseText);
          OpenNotification('Respuesta del servidor no válida', 'error');
          if (onError) {
            onError({ message: 'Invalid server response', rawResponse: responseText });
          }
          return;
        }
      }
      
      console.log(result);

      if (!response.ok) {
        if(response.status === 401) {
          console.log("unauthorized, logging out");
          onError('LOGOUT');
        }

        //const errorText = await response.text();
        OpenNotification(`${response.status} - ${result.message}`, 'error');
        if (onError) {
          onError(response);
        }
        return;
      }


      //Custom api logic error handling
      if(result.code !== 0) 
      {        
        OpenNotification(result.message, 'error');

        if (onError) {
          onError(result);
        }
        return true;
      }

      //On api custom logic success
      OpenNotification(result.message, 'success');

      if (onSuccess) {
        onSuccess(result.payload);
      }

      return true;
    } 
    catch (error) {
      console.log(error);
      var msg = "Error no identificado";

      if (error.response) {
        msg = "Error en respuesta de servidor: " + error.response;
      } else if (error.request) {
        msg = "Error en la conexión de internet";
      } else {
        msg = error?.message;
      }

      OpenNotification(msg, 'error');

      if (onError) {
        onError(error);
      }

      return;
    }
  }, [openNotificationModal]);

  return (
    <ApiFetcherContext.Provider value={fetchData}>
      {children}
    </ApiFetcherContext.Provider>
  );
}






//-------------------------------------
// This is the hook consumers will use
export function useApiFetcher() {
  const fetcher = useContext(ApiFetcherContext);
  if (!fetcher) {
    throw new Error("useApiFetcher must be used within an ApiFetcherProvider");
  }
  return fetcher;
}





//-------------------------------------
// This is a compatibility wrapper to maintain the same API
const ApiFetcher = async (options) => {
  console.warn(
    "Direct ApiFetcher call detected. This utility now requires React context. " +
    "Please update your code to use the ApiFetcherProvider and useApiFetcher hook."
  );
  
  // This will throw an error in production, but serves as a stopgap 
  // during migration to alert developers
  throw new Error("ApiFetcher must be used with React context. Wrap your app with ApiFetcherProvider.");
};

export default ApiFetcher;